function parseDate (dateStr) {
  const match = String(dateStr || '').match(/^(-?\d+)\/(\d+)\/(\d+)$/)
  if (match) {
    let year = parseInt(match[1])
    if (year === 0) year = 1
    return {
      year,
      month: parseInt(match[2]),
      day: parseInt(match[3])
    }
  }
  return { year: 1, month: 1, day: 1 }
}

function toDecimalDegrees (degrees, minutes, seconds) {
  return Number(degrees || 0) + Number(minutes || 0) / 60 + Number(seconds || 0) / 3600
}

function splitTime (timeStr, fallback = '00:00:00') {
  return String(timeStr || fallback).split(':').map(value => Number(value || 0))
}

function decimalDegreesToDms (decimalValue) {
  const numericValue = Number(decimalValue)
  if (!Number.isFinite(numericValue)) {
    return null
  }

  let degrees = Math.trunc(numericValue)
  const minuteFloat = (numericValue - degrees) * 60
  let minutes = Math.trunc(minuteFloat)
  let seconds = Number(((minuteFloat - minutes) * 60).toFixed(6))

  if (seconds >= 60) {
    seconds = 0
    minutes += 1
  }
  if (minutes >= 60) {
    minutes = 0
    degrees += 1
  }

  return {
    degrees,
    minutes,
    seconds
  }
}

function resolveCoordinateParts ({ degrees, minutes, seconds, decimalValue }) {
  const fallbackParts = decimalDegreesToDms(decimalValue)
  const hasValue = value => value !== undefined && value !== null && value !== ''
  return {
    degrees: hasValue(degrees) ? degrees : fallbackParts && fallbackParts.degrees,
    minutes: hasValue(minutes) ? minutes : fallbackParts && fallbackParts.minutes,
    seconds: hasValue(seconds) ? seconds : fallbackParts && fallbackParts.seconds
  }
}

const CONFIG_REQUEST_FIELDS = [
  'timezone',
  'address',
  'longitudeDeg',
  'longitudeMin',
  'longitudeSec',
  'latitudeDeg',
  'latitudeMin',
  'latitudeSec',
  'zuoshan',
  'hsys',
  'toleranceMatrix',
  'phaseToleranceRows',
  'tooltipRemarkMap',
  'natalDate',
  'natalTime',
  'natalAddress',
  'natalTimezone',
  'natalLongitudeDeg',
  'natalLongitudeMin',
  'natalLongitudeSec',
  'natalLatitudeDeg',
  'natalLatitudeMin',
  'natalLatitudeSec'
]

// 仅本地运行态字段（**绝不进入发给后端的 payload**）。
// lunarDate      = 「农历」行：万年历口径（北京时间 24:00 分界），前端独立计算、用户可改，不影响任何计算；
// lunarDateXYJ   = 「星雲鑑历」行：星雲鑑口径（真太阳时/北京时间 23:00 分界），由后端 /fateCalculate 回填，只读。
const RUNTIME_REQUEST_FIELDS = [
  'date',
  'time',
  'name',
  'lunarDate',
  'lunarDateXYJ',
  'astrolabeType',
  'natalName',
  'natalLunarDate',
  'natalLunarDateXYJ'
]

const LEGACY_NEBULA_LOGIC_POINT_ID = 46
const FRONTEND_DUMMY_YOUYI_POINT_ID = 9000046

function cloneArray (value) {
  return Array.isArray(value) ? JSON.parse(JSON.stringify(value)) : []
}

function cloneObject (value) {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? JSON.parse(JSON.stringify(value))
    : {}
}

function normalizeComparableMap (value) {
  return Object.keys(cloneObject(value))
    .sort()
    .reduce((result, key) => {
      result[key] = value[key]
      return result
    }, {})
}

function normalizeNumericMap (value) {
  return Object.keys(cloneObject(value))
    .sort()
    .reduce((result, key) => {
      result[key] = roundNumber(value[key], 6)
      return result
    }, {})
}

function roundNumber (value, digits = 6) {
  if (value === undefined || value === null || value === '') {
    return undefined
  }
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) {
    return value
  }
  return Number(numericValue.toFixed(digits))
}

function sortPrimitiveArray (value) {
  return cloneArray(value).sort((left, right) => {
    if (left === right) return 0
    return left > right ? 1 : -1
  })
}

function sanitizeLogicPointIdsForBackend (value) {
  return sortPrimitiveArray(cloneArray(value)
    .map(item => Number(item))
    .filter(item => Number.isFinite(item) && item !== LEGACY_NEBULA_LOGIC_POINT_ID && item !== FRONTEND_DUMMY_YOUYI_POINT_ID))
}

function normalizeObjectArray (value, sortKeyResolver) {
  return cloneArray(value)
    .map(item => normalizeComparableMap(item))
    .sort((left, right) => {
      const leftKey = sortKeyResolver(left)
      const rightKey = sortKeyResolver(right)
      if (leftKey === rightKey) return 0
      return leftKey > rightKey ? 1 : -1
    })
}

function normalizeMidpointPair (pair) {
  if (Array.isArray(pair)) {
    return {
      planet1Index: Number(pair[0]),
      planet1Name: pair[2] || '',
      planet2Index: Number(pair[1]),
      planet2Name: pair[3] || ''
    }
  }

  const source = cloneObject(pair)
  return {
    planet1Index: Number(source.planet1Index),
    planet1Name: source.planet1Name || '',
    planet2Index: Number(source.planet2Index),
    planet2Name: source.planet2Name || ''
  }
}

function normalizeMidpointPairs (value) {
  return cloneArray(value)
    .map(pair => normalizeMidpointPair(pair))
    .filter(pair => Number.isFinite(pair.planet1Index) && Number.isFinite(pair.planet2Index))
    .sort((left, right) => {
      if (left.planet1Index !== right.planet1Index) {
        return left.planet1Index - right.planet1Index
      }
      if (left.planet2Index !== right.planet2Index) {
        return left.planet2Index - right.planet2Index
      }
      return `${left.planet1Name}-${left.planet2Name}`.localeCompare(`${right.planet1Name}-${right.planet2Name}`)
    })
}

function normalizePhaseToleranceRows (value) {
  return cloneArray(value)
    .map(row => ({
      planetIndex: row.planetIndex,
      tolerances: cloneArray(row.tolerances).map(item => roundNumber(item, 6))
    }))
    .sort((left, right) => Number(left.planetIndex || 0) - Number(right.planetIndex || 0))
}

function buildDefaultConfigState ({
  defaultMatrix,
  defaultPhaseToleranceRows,
  defaultSelectedPlanets,
  defaultSelectedStars,
  defaultSelectedPoints,
  defaultSelectedMajorPhases,
  defaultSelectedMinorPhases,
  defaultSelectedMidpoints,
  defaultStepValue,
  defaultStepUnit
}) {
  return {
    timezone: 8,
    address: '',
    longitudeDeg: 116,
    longitudeMin: 24,
    longitudeSec: 0,
    latitudeDeg: 39,
    latitudeMin: 54,
    latitudeSec: 0,
    zuoshan: 0,
    hsys: 'P',
    toleranceMatrix: cloneArray(defaultMatrix),
    phaseToleranceRows: cloneArray(defaultPhaseToleranceRows),
    tooltipRemarkMap: {},
    selectedPlanets: cloneArray(defaultSelectedPlanets),
    selectedStars: cloneArray(defaultSelectedStars),
    selectedPoints: cloneArray(defaultSelectedPoints),
    selectedMajorPhases: cloneArray(defaultSelectedMajorPhases),
    selectedMinorPhases: cloneArray(defaultSelectedMinorPhases),
    selectedMidpoints: cloneArray(defaultSelectedMidpoints),
    selectedAsteroids: [],
    stepIntervalValue: Number(defaultStepValue || 1),
    stepIntervalUnit: defaultStepUnit || 'minute',
    natalDate: '',
    natalTime: '',
    natalAddress: '',
    natalTimezone: 8,
    natalLongitudeDeg: 116,
    natalLongitudeMin: 24,
    natalLongitudeSec: 0,
    natalLatitudeDeg: 39,
    natalLatitudeMin: 54,
    natalLatitudeSec: 0,
    starToleranceMap: null
  }
}

function buildDefaultRuntimeState ({ dateStr, timeStr }) {
  return {
    date: dateStr,
    time: timeStr,
    name: '',
    lunarDate: '',
    lunarDateXYJ: '',
    astrolabeType: 'ecliptic',
    natalName: '',
    natalLunarDate: '',
    natalLunarDateXYJ: ''
  }
}

function extractRuntimeStateFromRequestData ({ requestData }) {
  return RUNTIME_REQUEST_FIELDS.reduce((state, field) => {
    state[field] = requestData[field]
    return state
  }, {})
}

function buildRequestDataFromPageState ({ requestData = {}, configState = {}, runtimeState = {} }) {
  return {
    ...requestData,
    ...configState,
    ...runtimeState
  }
}

function extractConfigStateFromViewState ({
  requestData,
  selectedPlanets = [],
  selectedStars = [],
  selectedPoints = [],
  selectedMajorPhases = [],
  selectedMinorPhases = [],
  selectedMidpoints = [],
  stepValue = 1,
  stepUnit = 'minute'
}) {
  const configState = CONFIG_REQUEST_FIELDS.reduce((state, field) => {
    state[field] = requestData[field]
    return state
  }, {})

  configState.selectedPlanets = cloneArray(selectedPlanets)
  configState.selectedStars = cloneArray(selectedStars)
  configState.selectedPoints = cloneArray(selectedPoints)
  configState.selectedMajorPhases = cloneArray(selectedMajorPhases)
  configState.selectedMinorPhases = cloneArray(selectedMinorPhases)
  configState.selectedMidpoints = cloneArray(selectedMidpoints)
  configState.selectedAsteroids = cloneArray(requestData.selectedAsteroids)
  configState.stepIntervalValue = Number(stepValue || 1)
  configState.stepIntervalUnit = stepUnit || 'minute'
  configState.starToleranceMap = requestData.starToleranceMap || null
  configState.tooltipRemarkMap = cloneObject(requestData.tooltipRemarkMap)
  return configState
}

function applyFormDataPatchToPageState ({ configState, runtimeState, formData }) {
  const nextConfigState = { ...configState }
  const nextRuntimeState = { ...runtimeState }
  let configChanged = false

  Object.keys(formData || {}).forEach(field => {
    if (CONFIG_REQUEST_FIELDS.includes(field)) {
      nextConfigState[field] = formData[field]
      configChanged = true
    }
    if (RUNTIME_REQUEST_FIELDS.includes(field)) {
      nextRuntimeState[field] = formData[field]
    }
  })

  return {
    configState: nextConfigState,
    runtimeState: nextRuntimeState,
    configChanged
  }
}

function buildBaseRequestPayload ({ requestData, selectedPlanets, selectedStars, selectedPoints }) {
  const { year, month, day } = parseDate(requestData.date)
  const [hour, minute, second] = splitTime(requestData.time)

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    timezone: requestData.timezone,
    longitude: toDecimalDegrees(requestData.longitudeDeg, requestData.longitudeMin, requestData.longitudeSec),
    latitude: toDecimalDegrees(requestData.latitudeDeg, requestData.latitudeMin, requestData.latitudeSec),
    hsys: requestData.hsys.codePointAt(0),
    astrolabeType: requestData.astrolabeType,
    selectedPlanets,
    selectedStars,
    selectedPoints: sanitizeLogicPointIdsForBackend(selectedPoints),
    zuoshan: requestData.zuoshan,
    toleranceMatrix: requestData.toleranceMatrix,
    phaseToleranceRows: requestData.phaseToleranceRows,
    // 逐颗恒星容许度（starNum -> 度），供星盘服务覆盖 BOStarInfo.tolerance（恒星报警阈值）。
    // 未配置时下发 {}，星盘侧等价于"全部默认 1.0"，与改造前行为一致。
    starToleranceMap: normalizeNumericMap(requestData.starToleranceMap)
  }
}

function buildFateRequestPayload ({ requestData, selectedPlanets, selectedStars, selectedPoints }) {
  return buildBaseRequestPayload({
    requestData,
    selectedPlanets,
    selectedStars,
    selectedPoints
  })
}

function buildGenerateRequestPayload ({
  requestData,
  selectedPlanets,
  selectedStars,
  selectedPoints,
  selectedMajorPhases,
  selectedMinorPhases,
  selectedMidpoints
}) {
  const payload = {
    ...buildBaseRequestPayload({
      requestData,
      selectedPlanets,
      selectedStars,
      selectedPoints
    }),
    selectedMajorPhases,
    selectedMinorPhases,
    selectedMidpoints
  }

  if (['ecliptic', 'horizon_ecliptic'].includes(requestData.astrolabeType) && requestData.natalDate) {
    const { year, month, day } = parseDate(requestData.natalDate)
    payload.natalData = {
      year,
      month,
      day,
      longitude: toDecimalDegrees(requestData.natalLongitudeDeg, requestData.natalLongitudeMin, requestData.natalLongitudeSec),
      latitude: toDecimalDegrees(requestData.natalLatitudeDeg, requestData.natalLatitudeMin, requestData.natalLatitudeSec)
    }
  }

  return payload
}

function buildGenerateChartInput ({
  requestData,
  selectedPlanets,
  selectedStars,
  selectedPoints,
  selectedMidpoints
}) {
  return {
    ...buildBaseRequestPayload({
      requestData,
      selectedPlanets,
      selectedStars,
      selectedPoints
    }),
    selectedMidpoints: cloneArray(selectedMidpoints)
  }
}

function buildGenerateInputSignature (input = {}) {
  return JSON.stringify({
    year: Number(input.year || 0),
    month: Number(input.month || 0),
    day: Number(input.day || 0),
    hour: Number(input.hour || 0),
    minute: Number(input.minute || 0),
    second: Number(input.second || 0),
    timezone: Number(input.timezone || 0),
    longitude: roundNumber(input.longitude),
    latitude: roundNumber(input.latitude),
    hsys: input.hsys,
    astrolabeType: input.astrolabeType || '',
    selectedPlanets: sortPrimitiveArray(input.selectedPlanets),
    selectedStars: sortPrimitiveArray(input.selectedStars),
    selectedPoints: sortPrimitiveArray(input.selectedPoints),
    toleranceMatrix: cloneArray(input.toleranceMatrix),
    phaseToleranceRows: normalizePhaseToleranceRows(input.phaseToleranceRows),
    selectedMidpoints: normalizeMidpointPairs(input.selectedMidpoints)
  })
}

function buildGenerateBundlePayload ({
  requestData,
  selectedPlanets,
  selectedStars,
  selectedPoints,
  selectedMidpoints,
  shouldFetchNatal = false
}) {
  const mainInput = buildGenerateChartInput({
    requestData,
    selectedPlanets,
    selectedStars,
    selectedPoints,
    selectedMidpoints
  })

  const payload = {
    referenceChartId: 'main',
    charts: [
      {
        chartId: 'main',
        role: 'main',
        input: mainInput
      }
    ]
  }

  let natalInput = null
  let reuseNatalFromMain = false
  if (shouldFetchNatal && requestData.natalDate) {
    natalInput = buildNatalGeneratePayload({
      requestData,
      natalSelectedPlanets: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 1000014, 1000015]
    })

    if (buildGenerateInputSignature(mainInput) === buildGenerateInputSignature(natalInput)) {
      reuseNatalFromMain = true
    } else {
      payload.charts.push({
        chartId: 'natal',
        role: 'natal',
        input: natalInput
      })
    }
  }

  return {
    payload,
    mainInput,
    natalInput,
    shouldFetchNatal: Boolean(shouldFetchNatal && requestData.natalDate),
    reuseNatalFromMain
  }
}

function buildNatalGeneratePayload ({ requestData, natalSelectedPlanets }) {
  const { year, month, day } = parseDate(requestData.natalDate)
  const [hour, minute, second] = splitTime(requestData.natalTime)

  return {
    year,
    month,
    day,
    hour,
    minute,
    second,
    timezone: requestData.natalTimezone || requestData.timezone,
    longitude: toDecimalDegrees(requestData.natalLongitudeDeg, requestData.natalLongitudeMin, requestData.natalLongitudeSec),
    latitude: toDecimalDegrees(requestData.natalLatitudeDeg, requestData.natalLatitudeMin, requestData.natalLatitudeSec),
    hsys: requestData.hsys.codePointAt(0),
    astrolabeType: 'ecliptic',
    selectedPlanets: natalSelectedPlanets,
    selectedStars: [],
    selectedPoints: [],
    zuoshan: requestData.zuoshan,
    toleranceMatrix: null,
    phaseToleranceRows: [],
    // 本命盘不选恒星，故恒星容许度与行星相位容许度一样显式置空
    starToleranceMap: null,
    selectedMajorPhases: [],
    selectedMinorPhases: [],
    selectedMidpoints: []
  }
}

function buildSaveConfigPayload ({ configState, requestData, selectedPlanets, selectedStars, selectedPoints, selectedMajorPhases, selectedMinorPhases, selectedMidpoints, stepValue, stepUnit }) {
  const resolvedConfigState = configState || extractConfigStateFromViewState({
    requestData,
    selectedPlanets,
    selectedStars,
    selectedPoints,
    selectedMajorPhases,
    selectedMinorPhases,
    selectedMidpoints,
    stepValue,
    stepUnit
  })

  const rawPayload = {
    defaultLongitude: toDecimalDegrees(resolvedConfigState.longitudeDeg, resolvedConfigState.longitudeMin, resolvedConfigState.longitudeSec),
    defaultLatitude: toDecimalDegrees(resolvedConfigState.latitudeDeg, resolvedConfigState.latitudeMin, resolvedConfigState.latitudeSec),
    defaultTimezone: resolvedConfigState.timezone,
    address: resolvedConfigState.address,
    longitudeDeg: resolvedConfigState.longitudeDeg,
    longitudeMin: resolvedConfigState.longitudeMin,
    longitudeSec: resolvedConfigState.longitudeSec,
    latitudeDeg: resolvedConfigState.latitudeDeg,
    latitudeMin: resolvedConfigState.latitudeMin,
    latitudeSec: resolvedConfigState.latitudeSec,
    zuoshan: resolvedConfigState.zuoshan,
    hsys: resolvedConfigState.hsys,
    stepIntervalUnit: resolvedConfigState.stepIntervalUnit,
    stepIntervalValue: resolvedConfigState.stepIntervalValue,
    selectedPlanets: resolvedConfigState.selectedPlanets,
    selectedStars: resolvedConfigState.selectedStars,
    selectedMajorPhases: resolvedConfigState.selectedMajorPhases,
    selectedMinorPhases: resolvedConfigState.selectedMinorPhases,
    selectedMidpoints: resolvedConfigState.selectedMidpoints,
    selectedLogicPoints: sanitizeLogicPointIdsForBackend(resolvedConfigState.selectedPoints),
    selectedAsteroids: resolvedConfigState.selectedAsteroids,
    planetToleranceMatrix: resolvedConfigState.toleranceMatrix,
    phaseToleranceRows: resolvedConfigState.phaseToleranceRows,
    starToleranceMap: resolvedConfigState.starToleranceMap,
    tooltipRemarkMap: resolvedConfigState.tooltipRemarkMap,
    natalDate: resolvedConfigState.natalDate,
    natalTime: resolvedConfigState.natalTime,
    natalAddress: resolvedConfigState.natalAddress,
    natalLongitude: toDecimalDegrees(resolvedConfigState.natalLongitudeDeg, resolvedConfigState.natalLongitudeMin, resolvedConfigState.natalLongitudeSec),
    natalLatitude: toDecimalDegrees(resolvedConfigState.natalLatitudeDeg, resolvedConfigState.natalLatitudeMin, resolvedConfigState.natalLatitudeSec),
    natalTimezone: resolvedConfigState.natalTimezone,
    natalLongitudeDeg: resolvedConfigState.natalLongitudeDeg,
    natalLongitudeMin: resolvedConfigState.natalLongitudeMin,
    natalLongitudeSec: resolvedConfigState.natalLongitudeSec,
    natalLatitudeDeg: resolvedConfigState.natalLatitudeDeg,
    natalLatitudeMin: resolvedConfigState.natalLatitudeMin,
    natalLatitudeSec: resolvedConfigState.natalLatitudeSec
  }

  return normalizePersistedConfig(rawPayload)
}

function mergeLoadedConfigIntoConfigState ({ configState, config }) {
  const nextConfigState = {
    ...configState
  }

  if (!config) {
    return nextConfigState
  }

  const defaultLongitudeParts = resolveCoordinateParts({
    degrees: config.longitudeDeg,
    minutes: config.longitudeMin,
    seconds: config.longitudeSec,
    decimalValue: config.defaultLongitude
  })
  const defaultLatitudeParts = resolveCoordinateParts({
    degrees: config.latitudeDeg,
    minutes: config.latitudeMin,
    seconds: config.latitudeSec,
    decimalValue: config.defaultLatitude
  })
  const natalLongitudeParts = resolveCoordinateParts({
    degrees: config.natalLongitudeDeg,
    minutes: config.natalLongitudeMin,
    seconds: config.natalLongitudeSec,
    decimalValue: config.natalLongitude
  })
  const natalLatitudeParts = resolveCoordinateParts({
    degrees: config.natalLatitudeDeg,
    minutes: config.natalLatitudeMin,
    seconds: config.natalLatitudeSec,
    decimalValue: config.natalLatitude
  })

  // 地址与其他表单字段同口径：后台有值就回灌到右侧「基本信息」「本命盘信息」的地址输入框。
  // （2026-10-17 起取消「每次进页面清空地址」的旧口径）
  // 注意：给 undefined（后台没有该字段）时，下方 merge 会跳过该字段，保留当前会话里的值。
  const statePatch = {
    timezone: config.defaultTimezone !== undefined ? config.defaultTimezone : config.timezone,
    address: config.address,
    longitudeDeg: defaultLongitudeParts.degrees,
    longitudeMin: defaultLongitudeParts.minutes,
    longitudeSec: defaultLongitudeParts.seconds,
    latitudeDeg: defaultLatitudeParts.degrees,
    latitudeMin: defaultLatitudeParts.minutes,
    latitudeSec: defaultLatitudeParts.seconds,
    zuoshan: config.zuoshan,
    hsys: config.hsys,
    toleranceMatrix: config.planetToleranceMatrix,
    phaseToleranceRows: config.phaseToleranceRows,
    tooltipRemarkMap: cloneObject(config.tooltipRemarkMap),
    selectedPlanets: config.selectedPlanets,
    selectedStars: config.selectedStars,
    selectedPoints: config.selectedLogicPoints || config.selectedPoints,
    selectedMajorPhases: config.selectedMajorPhases,
    selectedMinorPhases: config.selectedMinorPhases,
    selectedMidpoints: config.selectedMidpoints,
    selectedAsteroids: config.selectedAsteroids,
    stepIntervalValue: config.stepIntervalValue,
    stepIntervalUnit: config.stepIntervalUnit,
    natalDate: config.natalDate,
    natalTime: config.natalTime,
    natalAddress: config.natalAddress,
    natalTimezone: config.natalTimezone,
    natalLongitudeDeg: natalLongitudeParts.degrees,
    natalLongitudeMin: natalLongitudeParts.minutes,
    natalLongitudeSec: natalLongitudeParts.seconds,
    natalLatitudeDeg: natalLatitudeParts.degrees,
    natalLatitudeMin: natalLatitudeParts.minutes,
    natalLatitudeSec: natalLatitudeParts.seconds,
    starToleranceMap: config.starToleranceMap
  }

  Object.keys(statePatch).forEach(field => {
    if (statePatch[field] !== undefined && statePatch[field] !== null) {
      nextConfigState[field] = statePatch[field]
    }
  })

  return nextConfigState
}

function normalizePersistedConfig (config = {}) {
  const defaultLongitudeParts = resolveCoordinateParts({
    degrees: config.longitudeDeg,
    minutes: config.longitudeMin,
    seconds: config.longitudeSec,
    decimalValue: config.defaultLongitude
  })
  const defaultLatitudeParts = resolveCoordinateParts({
    degrees: config.latitudeDeg,
    minutes: config.latitudeMin,
    seconds: config.latitudeSec,
    decimalValue: config.defaultLatitude
  })
  const natalLongitudeParts = resolveCoordinateParts({
    degrees: config.natalLongitudeDeg,
    minutes: config.natalLongitudeMin,
    seconds: config.natalLongitudeSec,
    decimalValue: config.natalLongitude
  })
  const natalLatitudeParts = resolveCoordinateParts({
    degrees: config.natalLatitudeDeg,
    minutes: config.natalLatitudeMin,
    seconds: config.natalLatitudeSec,
    decimalValue: config.natalLatitude
  })

  return {
    defaultLongitude: roundNumber(toDecimalDegrees(defaultLongitudeParts.degrees, defaultLongitudeParts.minutes, defaultLongitudeParts.seconds)),
    defaultLatitude: roundNumber(toDecimalDegrees(defaultLatitudeParts.degrees, defaultLatitudeParts.minutes, defaultLatitudeParts.seconds)),
    defaultTimezone: config.defaultTimezone !== undefined ? config.defaultTimezone : config.timezone,
    address: config.address || '',
    longitudeDeg: defaultLongitudeParts.degrees,
    longitudeMin: defaultLongitudeParts.minutes,
    longitudeSec: roundNumber(defaultLongitudeParts.seconds),
    latitudeDeg: defaultLatitudeParts.degrees,
    latitudeMin: defaultLatitudeParts.minutes,
    latitudeSec: roundNumber(defaultLatitudeParts.seconds),
    zuoshan: roundNumber(config.zuoshan),
    hsys: config.hsys === null ? undefined : config.hsys,
    stepIntervalUnit: config.stepIntervalUnit || config.stepUnit || 'minute',
    stepIntervalValue: Number(config.stepIntervalValue !== undefined ? config.stepIntervalValue : (config.stepValue || 1)),
    selectedPlanets: sortPrimitiveArray(config.selectedPlanets),
    selectedStars: sortPrimitiveArray(config.selectedStars),
    selectedLogicPoints: sortPrimitiveArray(config.selectedLogicPoints || config.selectedPoints),
    selectedMajorPhases: normalizeObjectArray(config.selectedMajorPhases, item => `${item.ruleIndex ?? ''}:${item.name ?? ''}`),
    selectedMinorPhases: normalizeObjectArray(config.selectedMinorPhases, item => `${item.ruleIndex ?? ''}:${item.name ?? ''}`),
    selectedMidpoints: normalizeMidpointPairs(config.selectedMidpoints),
    selectedAsteroids: sortPrimitiveArray(config.selectedAsteroids),
    planetToleranceMatrix: cloneArray(config.planetToleranceMatrix || config.toleranceMatrix).map(row => cloneArray(row).map(item => roundNumber(item, 6))),
    phaseToleranceRows: normalizePhaseToleranceRows(config.phaseToleranceRows),
    starToleranceMap: normalizeNumericMap(config.starToleranceMap),
    tooltipRemarkMap: normalizeComparableMap(config.tooltipRemarkMap),
    natalDate: config.natalDate || '',
    natalTime: config.natalTime || '',
    natalAddress: config.natalAddress || '',
    natalLongitude: roundNumber(toDecimalDegrees(natalLongitudeParts.degrees, natalLongitudeParts.minutes, natalLongitudeParts.seconds)),
    natalLatitude: roundNumber(toDecimalDegrees(natalLatitudeParts.degrees, natalLatitudeParts.minutes, natalLatitudeParts.seconds)),
    natalTimezone: config.natalTimezone === null ? undefined : config.natalTimezone,
    natalLongitudeDeg: natalLongitudeParts.degrees,
    natalLongitudeMin: natalLongitudeParts.minutes,
    natalLongitudeSec: roundNumber(natalLongitudeParts.seconds),
    natalLatitudeDeg: natalLatitudeParts.degrees,
    natalLatitudeMin: natalLatitudeParts.minutes,
    natalLatitudeSec: roundNumber(natalLatitudeParts.seconds)
  }
}

function mergeLoadedConfigIntoRequestData ({ requestData, config }) {
  const nextRequestData = {
    ...requestData
  }

  const mergedConfigState = mergeLoadedConfigIntoConfigState({
    configState: requestData,
    config
  })

  return buildRequestDataFromPageState({
    requestData: nextRequestData,
    configState: mergedConfigState,
    runtimeState: extractRuntimeStateFromRequestData({ requestData })
  })
}

module.exports = {
  CONFIG_REQUEST_FIELDS,
  RUNTIME_REQUEST_FIELDS,
  parseDate,
  toDecimalDegrees,
  splitTime,
  buildDefaultConfigState,
  buildDefaultRuntimeState,
  extractConfigStateFromViewState,
  extractRuntimeStateFromRequestData,
  buildRequestDataFromPageState,
  applyFormDataPatchToPageState,
  buildFateRequestPayload,
  buildGenerateChartInput,
  buildGenerateInputSignature,
  buildGenerateBundlePayload,
  buildGenerateRequestPayload,
  buildNatalGeneratePayload,
  buildSaveConfigPayload,
  normalizePersistedConfig,
  mergeLoadedConfigIntoConfigState,
  mergeLoadedConfigIntoRequestData
}
